import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PiggyBank, Upload, HandCoins } from "lucide-react";
import api, { apiError, formatRupiah, formatTanggal } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_BADGE = {
  pending: { label: "Menunggu Konfirmasi", cls: "bg-amber-50 text-amber-700 hover:bg-amber-50" },
  approved: { label: "Disetujui", cls: "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" },
  rejected: { label: "Ditolak", cls: "bg-rose-50 text-rose-700 hover:bg-rose-50" },
};

export default function ParentSavings() {
  const [children, setChildren] = useState(null);
  const [requests, setRequests] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [amount, setAmount] = useState("");
  const [proof, setProof] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [sv, mine] = await Promise.all([api.get("/savings/my"), api.get("/payments/online/my")]);
      setChildren(sv.data);
      setRequests(mine.data.filter((p) => p.kind === "setor" || p.kind === "tarik"));
    } catch (err) {
      toast.error(apiError(err));
      setChildren([]);
    }
  };

  useEffect(() => { load(); }, []);

  const openDialog = (mode, c) => {
    setDialog({ mode, student: c.student, balance: c.balance });
    setAmount("");
    setProof(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (dialog.mode === "setor" && !proof) {
      toast.error("Unggah foto bukti setoran dulu");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("kind", dialog.mode);
      fd.append("student_id", dialog.student.id);
      fd.append("amount", amount);
      if (dialog.mode === "setor") fd.append("proof", proof);
      await api.post("/payments/online", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(dialog.mode === "setor"
        ? "Bukti setoran terkirim — menunggu konfirmasi bendahara"
        : "Permintaan tarik tabungan terkirim — menunggu konfirmasi bendahara");
      setDialog(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  if (children === null) {
    return <div className="py-20 text-center text-sm text-slate-500" data-testid="parent-savings-loading">Memuat data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="parent-savings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Tabungan Anak</h1>
        <p className="mt-1 text-sm text-slate-500">Setor tabungan via transfer/QRIS dengan bukti, atau ajukan penarikan — diproses setelah dikonfirmasi bendahara.</p>
      </div>

      {children.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="parent-savings-empty">
          <PiggyBank className="mx-auto mb-3 h-10 w-10 text-slate-300" strokeWidth={1.5} />
          <p className="font-heading text-lg font-semibold text-slate-800">Belum ada anak yang tertaut</p>
          <p className="mt-1 text-sm text-slate-500">Hubungi bendahara sekolah untuk menautkan akun Anda ke data anak.</p>
        </div>
      )}

      {children.map((c) => (
        <div key={c.student.id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`savings-card-${c.student.nisn}`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-900">{c.student.name}</h2>
              <p className="text-sm text-slate-500">Kelas {c.student.class_name} · NISN {c.student.nisn}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-slate-400">Saldo Tabungan</p>
              <p className="font-heading text-2xl font-bold text-[#0060AF]" data-testid={`savings-balance-${c.student.nisn}`}>{formatRupiah(c.balance)}</p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={() => openDialog("setor", c)} data-testid={`setor-button-${c.student.nisn}`} className="gap-2 bg-[#0060AF] hover:bg-[#004B87] active:scale-[0.98]">
              <Upload className="h-4 w-4" strokeWidth={1.5} /> Setor Tabungan
            </Button>
            <Button variant="outline" onClick={() => openDialog("tarik", c)} disabled={c.balance <= 0}
                    data-testid={`tarik-button-${c.student.nisn}`}
                    className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50 active:scale-[0.98]">
              <HandCoins className="h-4 w-4" strokeWidth={1.5} /> Minta Tarik Tabungan
            </Button>
          </div>

          <div className="mt-5">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Riwayat Transaksi</h3>
            <div className="overflow-x-auto">
              <Table data-testid={`savings-table-${c.student.nisn}`}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tanggal</TableHead>
                    <TableHead>Jenis</TableHead>
                    <TableHead>Keterangan</TableHead>
                    <TableHead className="text-right">Jumlah</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {c.history.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="py-6 text-center text-sm text-slate-500">Belum ada transaksi tabungan.</TableCell></TableRow>
                  )}
                  {c.history.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="text-sm text-slate-600">{formatTanggal(t.created_at)}</TableCell>
                      <TableCell>
                        {t.type === "setor" ? (
                          <Badge className="rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-50">Setor</Badge>
                        ) : (
                          <Badge className="rounded-full bg-amber-50 text-amber-600 hover:bg-amber-50">Tarik</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{t.note || "-"}</TableCell>
                      <TableCell className="text-right text-sm font-medium text-slate-900">{formatRupiah(t.amount)}</TableCell>
                      <TableCell className="text-right text-sm text-slate-600">{formatRupiah(t.balance_after)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      ))}

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="savings-requests-card">
        <h3 className="font-heading text-base font-semibold text-slate-800">Riwayat Pengajuan Setor & Tarik</h3>
        <div className="mt-3 space-y-2">
          {requests.length === 0 && <p className="text-sm text-slate-400" data-testid="savings-requests-empty">Belum ada pengajuan setor/tarik tabungan.</p>}
          {requests.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-3" data-testid={`savings-request-row-${p.id}`}>
              <div>
                <p className="text-sm font-medium text-slate-800">
                  {p.kind === "setor" ? "Setor Tabungan" : "Tarik Tabungan"} — Rp {p.amount.toLocaleString("id-ID")}
                </p>
                <p className="text-xs text-slate-500">{p.student_name} · {new Date(p.created_at).toLocaleDateString("id-ID")}</p>
                {p.status === "rejected" && p.reject_reason && <p className="text-xs text-rose-500">Alasan: {p.reject_reason}</p>}
              </div>
              <Badge className={`rounded-full ${STATUS_BADGE[p.status]?.cls || ""}`} data-testid={`savings-request-status-${p.id}`}>
                {STATUS_BADGE[p.status]?.label || p.status}
              </Badge>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent aria-describedby={undefined} data-testid="savings-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {dialog?.mode === "setor" ? "Setor Tabungan" : "Minta Tarik Tabungan"} — {dialog?.student.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="savings-form">
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
              {dialog?.mode === "setor"
                ? "Transfer/scan QRIS sekolah (lihat menu Bayar Online), lalu unggah buktinya di sini. Saldo bertambah setelah bendahara mengonfirmasi."
                : `Saldo tersedia: Rp ${dialog?.balance.toLocaleString("id-ID")}. Dana diambil di sekolah setelah bendahara menyetujui.`}
            </div>
            <div className="space-y-2">
              <Label htmlFor="savings-amount">Jumlah (Rp)</Label>
              <Input id="savings-amount" type="number" min="1" max={dialog?.mode === "tarik" ? dialog?.balance : undefined}
                     data-testid="savings-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} required />
            </div>
            {dialog?.mode === "setor" && (
              <div className="space-y-2">
                <Label htmlFor="savings-proof">Foto Bukti Transfer / QRIS</Label>
                <Input id="savings-proof" type="file" accept=".jpg,.jpeg,.png,.webp" data-testid="savings-proof-input"
                       onChange={(e) => setProof(e.target.files?.[0] || null)} required />
                <p className="text-xs text-slate-400">Format jpg/png/webp, maksimal 2 MB.</p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(null)} data-testid="savings-cancel">Batal</Button>
              <Button type="submit" disabled={sending} data-testid="savings-submit"
                      className={dialog?.mode === "setor" ? "bg-[#0060AF] hover:bg-[#004B87]" : "bg-amber-600 text-white hover:bg-amber-700"}>
                {sending ? "Mengirim..." : dialog?.mode === "setor" ? "Kirim Bukti Setoran" : "Kirim Permintaan Tarik"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
